import { useCallback } from "react";

import { useAuthUser } from "@features/auth";
import { UserRole } from "@models/UserRole";

import roleScopes from "./roleScopes";
import { TScope } from "./scopes";

// type guard to check if the role has a defined scope
function roleHasDefinedScope(role: UserRole): role is keyof typeof roleScopes {
  return role in roleScopes;
}

/**
 * Returns a boolean indicating if the user has the given permission.
 * Falls back to the legacy condition or `true` if the role doesn't have a defined scope.
 * */
export const roleHasPermission = (
  role: UserRole,
  permission: TScope | TScope[] | undefined,
  legacyCondition?: boolean
) => {
  if (!permission || !roleHasDefinedScope(role)) return legacyCondition ?? true;
  const permissions = ([] as TScope[]).concat(permission);

  return permissions.every((p) => roleScopes[role].has(p));
};

/**
 * Returns a function that checks if the user has the given permission.
 * Falls back to the legacy condition or `true` if the role doesn't have a defined scope.
 * */
export const useHasPermission = () => {
  const { role } = useAuthUser();

  /**
   * Returns a boolean indicating if the user has the given permission.
   * Falls back to the legacy condition or `true` if the role doesn't have a defined scope.
   * */
  const hasPermission = useCallback(
    (permission: TScope | TScope[], legacyCondition?: boolean): boolean =>
      roleHasPermission(role, permission, legacyCondition),
    [role]
  );

  return hasPermission;
};
